/* eslint-disable @typescript-eslint/no-explicit-any */
// WebSocket-driven replacement for the legacy polling conversation web worker.
// Subscribes to #message#{convId} for the active conversation and #conversation#
// for the conversation list, then fetches only the changed item on each event —
// no periodic polling.
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type { WebSocketMessage } from '@/hooks/useWebSocket';
import { useAuth } from '@/state/auth';
import type { Conversation, Message } from '@/types';

interface PendingMessage {
  id: string;
  text: string;
  timestamp: number;
}

interface ChatWorkerState {
  conversations: Conversation[];
  messages: Message[];
  pendingMessages: PendingMessage[];
  conversationsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  sendMessage: (
    conversationId: string,
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<void>;
  createConversation: (
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<any>;
}

const PENDING_TIMEOUT_MS = 30_000;

// Extract the conversationId from a key shaped like "#message#{convId}#{msgId}".
function conversationIdFromMessageKey(key: string): string | null {
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'message') {
    return parts[1];
  }
  return null;
}

// Extract the conversationId from a key shaped like "#conversation#{convId}".
function conversationIdFromConversationKey(key: string): string | null {
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'conversation') {
    return parts[1];
  }
  return null;
}

function conversationMatchesId(c: Conversation, convId: string): boolean {
  return c.uuid === convId || c.id === convId || (c.key || '').includes(convId);
}

// ENG-3112: WebSocket INSERT event deduplication (no code change required).
//
// The SQS BatchItemFailures retry mechanism on the backend may redeliver the
// same DynamoDB INSERT event over the WebSocket one or more additional times.
// Deduplication is handled at two layers:
//
//   1. mergeConversation (below) and mergeMessage (below) both key on
//      `item.key` — the DynamoDB sort key. A redelivered event for an already-
//      present item lands at the same index and replaces the existing entry
//      with an identical object, producing no visible state change.
//
//   2. On the invalidation path (useRealtimeSubscription -> debouncedInvalidate
//      -> queryClient.invalidateQueries), TanStack Query's in-flight deduplication
//      prevents multiple identical in-flight fetches even if invalidate fires
//      several times in rapid succession for the same query key.
//
// No runtime deduplication guard (e.g. a Set of seen keys) is needed here;
// the merge-by-key pattern is sufficient.

function mergeConversation(
  list: Conversation[],
  updated: Conversation
): Conversation[] {
  const idx = list.findIndex(c => c.key === updated.key);
  const next =
    idx >= 0
      ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)]
      : [updated, ...list];
  return next.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
}

function mergeMessage(list: Message[], updated: Message): Message[] {
  const idx = list.findIndex(m => m.key === updated.key);
  const next =
    idx >= 0
      ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)]
      : [...list, updated];
  return next.sort(
    (a, b) =>
      new Date(a.timestamp || '').getTime() -
      new Date(b.timestamp || '').getTime()
  );
}

function pendingMatchesRealMessage(
  pending: PendingMessage,
  real: Message
): boolean {
  if (real.role !== 'user') return false;
  if (real.id === pending.id || real.uuid === pending.id) return true;
  // Fallback: content match within 5s of pending send time
  if (real.content === pending.text) {
    const realTs = new Date(real.timestamp || 0).getTime();
    if (Math.abs(realTs - pending.timestamp) < 5000) return true;
  }
  return false;
}

export function useChatWorker(conversationId?: string): ChatWorkerState {
  const axios = useAxios();
  const { isImpersonating, friend, me } = useAuth();
  const currentUser = isImpersonating ? friend : me;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs avoid re-subscribing WS handlers on every state change.
  const activeConvIdRef = useRef<string | undefined>(conversationId);
  activeConvIdRef.current = conversationId;
  const pendingByConvRef = useRef<Map<string, PendingMessage[]>>(new Map());
  const pendingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const previousUserRef = useRef<string>('');

  const syncPendingForActive = useCallback(() => {
    const convId = activeConvIdRef.current;
    if (!convId) {
      setPendingMessages([]);
      return;
    }
    setPendingMessages([...(pendingByConvRef.current.get(convId) || [])]);
  }, []);

  const removePending = useCallback(
    (convId: string, messageId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(p => p.id !== messageId);
      if (remaining.length !== existing.length) {
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
      const timer = pendingTimersRef.current.get(messageId);
      if (timer) {
        clearTimeout(timer);
        pendingTimersRef.current.delete(messageId);
      }
    },
    [syncPendingForActive]
  );

  const prunePendingForMessage = useCallback(
    (real: Message, convId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(
        p => !pendingMatchesRealMessage(p, real)
      );
      if (remaining.length !== existing.length) {
        // Clear any timers for pruned entries
        for (const p of existing) {
          if (!remaining.includes(p)) {
            const timer = pendingTimersRef.current.get(p.id);
            if (timer) {
              clearTimeout(timer);
              pendingTimersRef.current.delete(p.id);
            }
          }
        }
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
    },
    [syncPendingForActive]
  );

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get('/my', {
        params: { label: 'conversation', key: 'parent_id:self', user: 'true' },
      });
      const list = (
        (response.data.conversations || []) as Conversation[]
      ).filter(c => !c.huntId);
      // Functional update: merge rather than replace so a concurrent WS-driven
      // mergeConversation isn't overwritten by a slightly-stale fetch snapshot.
      setConversations(prev => {
        const byKey = new Map(prev.map(c => [c.key, c]));
        for (const c of list) {
          byKey.set(c.key, c);
        }
        return [...byKey.values()].sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
      });
      setConversationsLoading(false);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch conversations'
      );
      setConversationsLoading(false);
    }
  }, [axios]);

  const fetchAndMergeConversation = useCallback(
    async (convId: string) => {
      try {
        const response = await axios.get('/my', {
          params: {
            label: 'conversation',
            key: `#conversation#${convId}`,
            user: 'true',
          },
        });
        const list = (response.data.conversations || []) as Conversation[];
        if (list.length === 0) {
          setConversations(prev =>
            prev.filter(c => !conversationMatchesId(c, convId))
          );
          return;
        }
        const conv = list[0];
        // Skip subagent conversations — only root conversations in the sidebar.
        if (conv.parent_id && conv.parent_id !== 'self') return;
        // Skip hunt iteration conversations — those live in the Hunts tab, not
        // the main Marcus assistant list (they share parent_id=self because
        // they're semantically root, but they're viewed per-hunt).
        if (conv.huntId) return;
        setConversations(prev => mergeConversation(prev, conv));
      } catch (err) {
        console.error('Failed to fetch conversation', convId, err);
      }
    },
    [axios]
  );

  const fetchMessages = useCallback(
    async (convId: string) => {
      try {
        setMessagesLoading(true);
        const response = await axios.get('/my', {
          params: {
            label: 'message',
            key: `#message#${convId}`,
            user: 'true',
          },
        });
        const list = (response.data.messages || []) as Message[];
        if (activeConvIdRef.current !== convId) {
          // Conversation switched mid-flight — drop the result.
          return;
        }
        // Functional update: merge fetched messages into current state rather
        // than replacing.  This prevents a concurrent WS-driven mergeMessage
        // from being overwritten by a slightly-stale fetch snapshot.
        setMessages(prev => {
          const byKey = new Map(prev.map(m => [m.key, m]));
          for (const m of list) {
            byKey.set(m.key, m);
          }
          return [...byKey.values()].sort(
            (a, b) =>
              new Date(a.timestamp || '').getTime() -
              new Date(b.timestamp || '').getTime()
          );
        });
        // Prune any pending that already made it into the list
        for (const m of list) {
          prunePendingForMessage(m, convId);
        }
        setMessagesLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch messages'
        );
        setMessagesLoading(false);
      }
    },
    [axios, prunePendingForMessage]
  );

  // Fetch a single message by its exact key and merge into state.
  const fetchAndMergeMessage = useCallback(
    async (key: string) => {
      const convId = conversationIdFromMessageKey(key);
      if (!convId) return;
      // Only bother if this is the active conversation — otherwise the next
      // conversation switch will do a full fetch anyway.
      if (convId !== activeConvIdRef.current) return;

      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key, user: 'true' },
        });
        const list = (response.data.messages || []) as Message[];
        if (activeConvIdRef.current !== convId) return;

        if (list.length === 0) {
          setMessages(prev => prev.filter(m => m.key !== key));
          return;
        }
        const updated = list[0];
        setMessages(prev => mergeMessage(prev, updated));
        prunePendingForMessage(updated, convId);
      } catch (err) {
        console.error('Failed to fetch message', key, err);
      }
    },
    [axios, prunePendingForMessage]
  );

  // Reset state and refetch the conversation list on user / account switch.
  useEffect(() => {
    if (!currentUser) return;
    if (previousUserRef.current && previousUserRef.current !== currentUser) {
      setConversations([]);
      setMessages([]);
      setPendingMessages([]);
      pendingByConvRef.current.clear();
      for (const timer of pendingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      pendingTimersRef.current.clear();
      setConversationsLoading(true);
      setMessagesLoading(false);
      setError(null);
    }
    previousUserRef.current = currentUser;
    fetchConversations();
  }, [currentUser, fetchConversations]);

  // WS subscriptions are declared BEFORE the initial fetch effects so that
  // React runs the subscribe effects first on the same render cycle.  This
  // eliminates the window where a message written between fetch-response and
  // subscribe-activation would be missed.  Any duplicate delivered by WS
  // during the fetch is deduplicated by mergeMessage (keyed on item key).

  // Live message updates for the active conversation. The WS payload includes
  // the full item serialized via TableSearch (same shape as /my), so we can
  // merge directly into state without an HTTP round-trip.
  const handleMessageEvent = useCallback(
    (ws: WebSocketMessage) => {
      if (ws.eventName === 'REMOVE') {
        setMessages(prev => prev.filter(m => m.key !== ws.key));
        return;
      }
      const convId = conversationIdFromMessageKey(ws.key);
      if (!convId || convId !== activeConvIdRef.current) return;

      // Extract the message from the TableSearch-shaped item payload.
      const messages = (ws.item?.messages ?? []) as Message[];
      if (messages.length > 0) {
        const msg = messages[0];
        setMessages(prev => mergeMessage(prev, msg));
        prunePendingForMessage(msg, convId);
        return;
      }
      // Fallback: fetch if the item wasn't included (e.g. older backend).
      fetchAndMergeMessage(ws.key);
    },
    [fetchAndMergeMessage, prunePendingForMessage]
  );
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#message#${conversationId}`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: handleMessageEvent,
  });

  // Live conversation-list updates (new conversations, status changes, deletes).
  // Only root conversations (parent_id === "self") are merged into the sidebar
  // list — subagent conversations are handled by useGetChildConversations.
  const handleConversationEvent = useCallback(
    (ws: WebSocketMessage) => {
      const convId = conversationIdFromConversationKey(ws.key);
      if (!convId) return;
      if (ws.eventName === 'REMOVE') {
        setConversations(prev =>
          prev.filter(c => !conversationMatchesId(c, convId))
        );
        return;
      }
      // Use the inline item if available; fall back to HTTP fetch.
      const conversations = (ws.item?.conversations ?? []) as Conversation[];
      if (conversations.length > 0) {
        const conv = conversations[0];
        // Skip subagent conversations — they belong in the child list, not
        // the root sidebar.
        if (conv.parent_id && conv.parent_id !== 'self') return;
        setConversations(prev => mergeConversation(prev, conv));
        return;
      }
      // HTTP fallback — fetchAndMergeConversation checks parent_id internally.
      fetchAndMergeConversation(convId);
    },
    [fetchAndMergeConversation]
  );
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    onUpdate: handleConversationEvent,
  });

  // Initial fetches run AFTER subscriptions are established above.
  useEffect(() => {
    if (conversationId) {
      setMessages([]);
      syncPendingForActive();
      fetchMessages(conversationId);
    } else {
      setMessages([]);
      setPendingMessages([]);
      setMessagesLoading(false);
    }
  }, [conversationId, fetchMessages, syncPendingForActive]);

  const sendMessage = useCallback(
    async (convId: string, text: string, mode?: 'query' | 'agent') => {
      const messageId = crypto.randomUUID();
      const pending: PendingMessage = {
        id: messageId,
        text,
        timestamp: Date.now(),
      };
      const list = pendingByConvRef.current.get(convId) || [];
      const nextList = [...list, pending];
      pendingByConvRef.current.set(convId, nextList);
      if (activeConvIdRef.current === convId) {
        syncPendingForActive();
      }

      // Safety: drop stuck pending entry after timeout so the UI doesn't
      // show "sending..." forever if something goes sideways.
      const timer = setTimeout(
        () => removePending(convId, messageId),
        PENDING_TIMEOUT_MS
      );
      pendingTimersRef.current.set(messageId, timer);

      try {
        const response = await axios.post('/planner', {
          conversationId: convId,
          message: text,
          mode: mode || 'query',
          messageId,
        });
        if (!response.data?.response?.success) {
          throw new Error('Message failed to send');
        }
      } catch (err) {
        removePending(convId, messageId);
        throw err;
      }
    },
    [axios, removePending, syncPendingForActive]
  );

  const createConversation = useCallback(
    async (message: string, mode?: 'query' | 'agent') => {
      const response = await axios.post('/planner', {
        conversationId: '',
        message,
        mode: mode || 'query',
      });
      if (!response.data?.conversation) {
        throw new Error('No conversation returned from server');
      }
      return response.data.conversation;
    },
    [axios]
  );

  // Cleanup pending timers on unmount.
  useEffect(() => {
    return () => {
      for (const timer of pendingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      pendingTimersRef.current.clear();
    };
  }, []);

  return {
    conversations,
    messages,
    pendingMessages,
    conversationsLoading,
    messagesLoading,
    error,
    sendMessage,
    createConversation,
  };
}
